import { Component, OnInit, OnDestroy } from '@angular/core';
import { trigger, transition, style, animate, query, stagger } from '@angular/animations';
import { BudgetMapperService } from '../services/budget-mapper.service';
import { BudgetCalculationItem, SubScreenRequest } from '../models/budget-response.interface';
import { Router } from '@angular/router';

@Component({
  selector: 'nh-budget-calculation-subscreen',
  templateUrl: './budget-subscreen.component.html',
  styleUrls: ['./budget-subscreen.component.scss'],
  standalone: false,
 animations: [
     trigger('expandCollapse', [
       transition(':enter', [
         style({ height: '0', opacity: '0', overflow: 'hidden' }),
         animate('400ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '*', opacity: '1' }))
       ]),
       transition(':leave', [
         style({ height: '*', opacity: '1', overflow: 'hidden' }),
         animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '0', opacity: '0' }))
       ])
     ]),  
     trigger('fadeInStagger', [
       transition('* => *', [
         query(':enter', [
           style({ opacity: 0, transform: 'translateX(-10px)' }),
           stagger('50ms', [
             animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1, transform: 'translateX(0)' }))
           ])
         ], { optional: true })
       ])
     ])
   ]
})
export class BudgetSubscreenComponent implements OnInit, OnDestroy {
  title: string = '';
  titleTooltip: string = '';
  memberName: string = '';
  subScreenType: any | null = null;
  items: BudgetCalculationItem[] = [];
  expandedItems: { [key: string]: boolean } = {};

  isLoading: boolean = true;
  errorMessage: string = '';
  infoText: string = '';
  policyLinks: Array<{text: string, url: string}> = [];

  constructor(
    private router: Router,
    private budgetMapperService: BudgetMapperService
  ) {}


  ngOnInit(): void {
    // Get sub-screen type from route params
    const subScreenData = sessionStorage.getItem('budgetSubScreenData');
    if (subScreenData) {
      const parsedData = JSON.parse(subScreenData);
      this.subScreenType = parsedData?.subScreenType;
      this.setTitleAndInfoText(parsedData);
      this.loadSubScreenData(parsedData);
    }
  }

  ngOnDestroy(): void {
    sessionStorage.removeItem('budgetSubScreenData');
  }

  private setTitleAndInfoText(subScreenData: any): void {
    const type = subScreenData?.subScreenType;

    switch (type) {
      case 'inKindSupportAndMaintenance':
        this.title = 'IN_KIND_SUPPORT_MAINTENANCE_TITLE';
        this.titleTooltip = 'ISM_TOOLTIP';
        this.infoText = 'IN_KIND_SUPPORT_AND_MAINTENANCE_INFO';
        break;
        case 'deemedResources':
        const budgetType = subScreenData?.budgetType;
        this.title = 'DEEMED_RESOURCES_TITLE';
        this.titleTooltip = '';
        if (budgetType === 'ABD') {
          this.infoText = 'DEEMED_RESOURCES_ABD_INFO';
        } else if (budgetType === 'FM') {
          this.infoText = 'DEEMED_RESOURCES_FM_INFO';
        }
        break;
      case 'abdDeemedIncome':
        this.title = 'DEEMED_INCOME_TITLE';
        this.titleTooltip = '';
        this.infoText = 'DEEMED_INCOME_ABD_INFO';
        break;
      case 'qualifiedIncomeTrust':
        this.title = 'QUALIFIED_INCOME_TRUST_TITLE';
        this.titleTooltip = 'QIT_TOOLTIP';
        this.infoText = 'QUALIFIED_INCOME_TRUST_INFO';
        break;
      case 'fmDeemedIncome':
        this.title = 'DEEMED_INCOME_FM_TITLE';
        this.titleTooltip = '';
        this.infoText = 'DEEMED_INCOME_FM_INFO';
        break;
      case 'medicalSpenddown':
        this.title = 'MEDICAL_SPENDDOWN_TITLE';
        this.titleTooltip = '';
        this.infoText = 'MEDICAL_SPENDDOWN_INFO';
        break;
      default:
        this.title = '';
        this.titleTooltip = '';
        this.infoText = '';
    }
  }

  private loadSubScreenData(subScreenData: any): void {
    const apiPayload: SubScreenRequest = {
      indvId: subScreenData?.indvId,
      coe: subScreenData?.coe,
      subScreenType: subScreenData?.subScreenType,
      coReqSeq: subScreenData?.coReqSeq
    };

    this.budgetMapperService.getBudgetScreenInfo(apiPayload).subscribe({
      next: (response) => {
        this.items = this.mapSubScreenData(subScreenData?.subScreenType!, response);
      }
    });
  }


  private mapSubScreenData(type: any, data: any): BudgetCalculationItem[] {
    switch (type) {
      case 'inKindSupportAndMaintenance':
        return this.budgetMapperService.mapInKindSupportData(data);
      case 'abdDeemedIncome':
        return this.budgetMapperService.mapDeemedIncomeABDData(data);
      case 'deemedResources':
        return this.budgetMapperService.mapDeemedResources(data);
      case 'qualifiedIncomeTrust':
        return this.budgetMapperService.mapQualifiedIncomeTrustData(data);
      case 'fmDeemedIncome':
        return this.budgetMapperService.mapDeemedIncomeFMData(data);
      case 'medicalSpenddown':
        return this.budgetMapperService.mapMedicalSpenddownData(data);
      default:
        return [];
    }
  }

  goBack(): void {
    sessionStorage.removeItem('budgetSubScreenData');
    this.router.navigate(['/user-profile/budget-details']);
  }


  toggleItem(itemId: string): void {
    this.expandedItems[itemId] = !this.expandedItems[itemId];
  }

  isExpanded(itemId: string): boolean {
    return this.expandedItems[itemId] || false;
  }

  isChildSupportTable(tableData: any[]): boolean {
    if (!tableData || tableData.length === 0) {
      return false;
    }
    // Check if the first row has child support properties
    return tableData[0].hasOwnProperty('expensesPerson');
  }

  isResourceTable(item: BudgetCalculationItem): boolean {
    return item?.resourceType !== undefined && item?.resourceType !== null;
  }
}
